import React, { useEffect, useMemo, useState } from 'react';
import { isFedRamp } from '@atlassian/atl-context';
import type { ProjectType } from '@atlassian/jira-common-constants/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import AsyncOpenHelpPanelLinkThroughEventListener from '@atlassian/jira-help-panel-button/src/async.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { Environment } from '@atlassian/jira-shared-types/src/tenant-context.tsx';
import { SpaStatePageReady } from '@atlassian/jira-spa-state-controller/src/components/main.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import BootstrapError from './error.tsx';
import type { AutomationPerimeter, BootstrapEnv, StackContext } from './types.tsx';
import useStackContext from './use-stack-context.tsx';

const BOOTSTRAP_FILENAME = 'automationManagerBootstrap.pack.js';
let isBootStrapBeenAppended = false;

type Container = {
	id?: number | string;
	key?: string;
	type?: ProjectType;
};

/**
 * Works out the automation environment we are to run the application in, as this is different to the jira environment
 */
export const getAutomationEnv = (
	jiraEnvironment: Environment,
	isAutomationDevInstance: boolean,
): BootstrapEnv => {
	if (isAutomationDevInstance) {
		if (jiraEnvironment === 'staging') {
			return 'dev';
		}
		if (jiraEnvironment === 'prod') {
			return 'test';
		}
	}
	return jiraEnvironment === 'staging' ? 'staging' : 'prod';
};

/**
 * https://developer.atlassian.com/platform/bifrost/config/custom-domain/#custom-domain:-stating-your-own-identity
 * For fedramp, we return **environment type** rather than the **environment** itself.
 * @param environment
 * @param isFedRampInstance
 * @returns
 */
const getBifrostEnvironment = (environment: string, isFedRampInstance = false): string => {
	let bifrostEnvironment: string;

	if (environment === 'staging') {
		bifrostEnvironment = isFedRampInstance ? 'stg' : 'stg-east';
	} else {
		// it's empty for fedramp prod
		bifrostEnvironment = isFedRampInstance ? '' : 'prod-east';
	}

	return bifrostEnvironment;
};

/**
 * Gets the correct Bifrost url suffix based on the perimeter
 */
const getBifrostUrlSuffix = (isFedRampInstance: boolean): string =>
	isFedRampInstance ? 'frontend.cdn.atlassian-us-gov-mod.com' : 'frontend.public.atl-paas.net';

const generateBifrostBootstrapUrl = (env: BootstrapEnv, buildNumber: string): string => {
	const isFedRampInstance = isFedRamp();

	const bifrostUrlPrefix = 'https://automation-frontend';
	const bifrostEnvironment = getBifrostEnvironment(env, isFedRampInstance);
	const bifrostUrlSuffix = getBifrostUrlSuffix(isFedRampInstance);

	const bifrostBaseUrl = [bifrostUrlPrefix, bifrostEnvironment, bifrostUrlSuffix]
		// remove empty strings
		.filter((str) => str)
		.join('.');

	const bifrostUrl = [bifrostBaseUrl, 'assets', buildNumber, 'bootstrap', BOOTSTRAP_FILENAME].join(
		'/',
	);

	return bifrostUrl;
};

/**
 * Gets the correct script url for the corresponding automation environment
 */
export const getBootstrapSrc = (env: BootstrapEnv, buildNumber: string): string => {
	// Local dev source
	if (env === 'dev') {
		return `https://localhost:8082/${BOOTSTRAP_FILENAME}`;
	}

	return generateBifrostBootstrapUrl(env, buildNumber);
};

const getBifrostBaseUrl = (env: BootstrapEnv, perimeter: AutomationPerimeter): string => {
	const isFedRampInstance = perimeter === 'FEDRAMP';

	const bifrostUrlPrefix = 'https://automation-frontend';
	const bifrostEnvironment = getBifrostEnvironment(env, isFedRampInstance);
	const bifrostUrlSuffix = getBifrostUrlSuffix(isFedRampInstance);

	return (
		[bifrostUrlPrefix, bifrostEnvironment, bifrostUrlSuffix]
			// remove empty strings
			.filter((str) => str)
			.join('.')
	);
};

/**
 * Gets the correct script url for the corresponding automation environment
 */
export const getBifrostBootstrapSrc = (
	env: BootstrapEnv,
	perimeter: AutomationPerimeter,
): string => {
	// Local dev source
	if (env === 'dev') {
		return `https://localhost:8082/${BOOTSTRAP_FILENAME}`;
	}

	return `${getBifrostBaseUrl(env, perimeter)}/${BOOTSTRAP_FILENAME}`;
};

export type AutomationAppBodyProps = {
	automationEnvironment: BootstrapEnv;
	activationId: string;
	cloudId: string;
	stackContext: StackContext;
	container?: Container;
};

export type AutomationAppProps = {
	container?: Container;
};

const AutomationAppStackContextLoader: React.FC<AutomationAppProps> = ({ container }) => {
	const { activationId, cloudId } = useTenantContext();
	const jiraEnvironment = useEnvironment();
	const automationEnvironment = getAutomationEnv(jiraEnvironment, fg('automation-dev-helper'));

	useEffect(() => {
		if (performance) {
			performance.mark('automation-host-product-start');
		}
	}, []);

	const stackContext = useStackContext(cloudId, automationEnvironment);

	if (stackContext.status === 'loading') {
		// TODO: consider a better loading state, it's non-trivial as our bootstrap script also adds a loader and deletes it
		return null;
	}
	if (stackContext.status === 'error') {
		return (
			// TODO: i18n and analytics
			<BootstrapError title="Failed to load Automation" description={stackContext.error} />
		);
	}

	return (
		<AutomationAppBody
			automationEnvironment={automationEnvironment}
			activationId={activationId}
			cloudId={cloudId}
			stackContext={stackContext.value}
			container={container}
		/>
	);
};

const BOOTSTRAP_ERROR_MESSAGE = 'Sorry, something went wrong loading Automation. Please try again.';
const DEV_BOOTSTRAP_ERROR_MESSAGE = 'Are you running devWatch?';

const AutomationAppBody: React.FC<AutomationAppBodyProps> = ({
	automationEnvironment,
	activationId,
	cloudId,
	stackContext,
	container,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [bootStrapError, setBootStrapError] = useState<string | null>(null);

	useEffect(() => {
		if (!isBootStrapBeenAppended) {
			// eslint-disable-next-line jira/js/no-document-script, jira/jira-ssr/no-unchecked-globals-usage
			const script = document.createElement('script');

			const src = getBootstrapSrc(automationEnvironment, stackContext.buildNumber);
			const startTime = performance.now();
			script.src = src;
			script.async = true;
			script.onload = () => {
				const duration = performance.now() - startTime;
				fireOperationalAnalytics(
					createAnalyticsEvent({
						actionSubject: 'automationJiraEntry',
						action: 'loaded',
					}),
					'automationBootstrapFile loaded',
					{
						duration,
						automationEnvironment,
					},
				);
			};
			script.onerror = async () => {
				const duration = performance.now() - startTime;

				fireOperationalAnalytics(
					createAnalyticsEvent({
						actionSubject: 'automationJiraEntry',
						action: 'failed',
					}),
					'automationBootstrapFile failed',
					{
						duration,
						automationEnvironment,
					},
				);
				setBootStrapError(
					automationEnvironment === 'dev' ? DEV_BOOTSTRAP_ERROR_MESSAGE : BOOTSTRAP_ERROR_MESSAGE,
				);
				throw new Error(
					`Failed to load automation boostrap for env ${automationEnvironment}, src was '${src}'`,
				);
			};

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.body?.appendChild(script);
			isBootStrapBeenAppended = true;
		}
	}, [automationEnvironment, createAnalyticsEvent, stackContext.buildNumber]);

	// Lifecycle events automation application can respond to start the application, and destroy it.
	useEffect(() => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.postMessage('automation-container-mounted');
		return () => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.postMessage('automation-container-unmounted');
		};
	}, []);

	if (bootStrapError) {
		return <BootstrapError title="Failed to load Automation" description={bootStrapError} />;
	}

	const context = {
		activationId,
		cloudId,
		scopeId: container?.id || 'GLOBAL',
		scopeKey: container?.key,
		scopeType: container?.type,
		productType: 'jira',
		env: automationEnvironment,
		staticPrefix: stackContext.staticPrefix,
		perimeter: stackContext.perimeter,
	};
	return (
		<>
			<AsyncOpenHelpPanelLinkThroughEventListener />
			<div
				id="automation-container"
				data-context={JSON.stringify(context)}
				data-testid="automation.automation-app-container"
			/>
			{/* SpaStatePageReady is used to indicate that the app can move to the "afterPaint" phase in react-loosely-lazy. */}
			<SpaStatePageReady />
		</>
	);
};

const determineAutomationPerimeter = (): AutomationPerimeter => {
	if (isFedRamp()) {
		return 'FEDRAMP';
	}
	return 'COMMERCIAL';
};

const AutomationAppComponent: React.FC<AutomationAppProps> = ({ container }) => {
	const { activationId, cloudId } = useTenantContext();
	const jiraEnvironment = useEnvironment();
	const automationEnvironment = getAutomationEnv(jiraEnvironment, fg('automation-dev-helper'));

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [bootStrapError, setBootStrapError] = useState<string | null>(null);
	const perimeter: AutomationPerimeter = useMemo(() => determineAutomationPerimeter(), []);

	useEffect(() => {
		if (performance) {
			performance.mark('automation-host-product-start');
		}
	}, []);

	useEffect(() => {
		if (!isBootStrapBeenAppended) {
			// eslint-disable-next-line jira/js/no-document-script, jira/jira-ssr/no-unchecked-globals-usage
			const script = document.createElement('script');

			const src = getBifrostBootstrapSrc(automationEnvironment, perimeter);
			const startTime = performance.now();
			script.src = src;
			script.async = true;
			script.onload = () => {
				const duration = performance.now() - startTime;
				fireOperationalAnalytics(
					createAnalyticsEvent({
						actionSubject: 'automationJiraEntry',
						action: 'loaded',
					}),
					'automationBootstrapFile loaded',
					{
						duration,
						automationEnvironment,
					},
				);
			};
			script.onerror = async () => {
				const duration = performance.now() - startTime;

				fireOperationalAnalytics(
					createAnalyticsEvent({
						actionSubject: 'automationJiraEntry',
						action: 'failed',
					}),
					'automationBootstrapFile failed',
					{
						duration,
						automationEnvironment,
					},
				);
				setBootStrapError(
					automationEnvironment === 'dev' ? DEV_BOOTSTRAP_ERROR_MESSAGE : BOOTSTRAP_ERROR_MESSAGE,
				);
				throw new Error(
					`Failed to load automation boostrap for env ${automationEnvironment}, src was '${src}'`,
				);
			};

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.body?.appendChild(script);
			isBootStrapBeenAppended = true;
		}
	}, [automationEnvironment, createAnalyticsEvent, perimeter]);

	// Lifecycle events automation application can respond to start the application, and destroy it.
	useEffect(() => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.postMessage('automation-container-mounted');
		return () => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.postMessage('automation-container-unmounted');
		};
	}, []);

	if (bootStrapError) {
		return <BootstrapError title="Failed to load Automation" description={bootStrapError} />;
	}

	const context = {
		activationId,
		cloudId,
		scopeId: container?.id || 'GLOBAL',
		scopeKey: container?.key,
		scopeType: container?.type,
		productType: 'jira',
		env: automationEnvironment,
		staticPrefix: getBifrostBaseUrl(automationEnvironment, perimeter),
		perimeter,
		isUsingVersionedAssets: true,
	};
	return (
		<>
			<AsyncOpenHelpPanelLinkThroughEventListener />
			<div
				id="automation-container"
				data-context={JSON.stringify(context)}
				data-testid="automation.automation-app-container"
			/>
			{/* SpaStatePageReady is used to indicate that the app can move to the "afterPaint" phase in react-loosely-lazy. */}
			<SpaStatePageReady />
		</>
	);
};

const AutomationApp: React.FC<AutomationAppProps> = ({ container }) => {
	return fg('jira_frontend_automation_versioned_assets') ? (
		<AutomationAppComponent container={container} />
	) : (
		<AutomationAppStackContextLoader container={container} />
	);
};

export default AutomationApp;
